import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { ReactComponent as Delete } from "../../../../assets/icons/svg/Cluva/delete.svg";
import { ReactComponent as Home } from "../../../../assets/icons/svg/home.svg";
import { ReactComponent as FullScreenLogo } from "../../../../assets/icons/svg/full screen.svg";
import { ReactComponent as MuteLogo } from "../../../../assets/icons/svg/mute.svg";
import { ReactComponent as UnmuteLogo } from "../../../../assets/icons/svg/unmute.svg";
import { ReactComponent as DownArrow } from "../../../../assets/icons/svg/down arrow.svg";
import { ReactComponent as CloseLogo } from "../../../../assets/icons/svg/close.svg";
import { ReactComponent as DeleteIcon } from "../../../../assets/icons/svg/delete-icon.svg";
import { ReactComponent as EditPreview } from "../../../../assets/icons/svg/Edit_Preview.svg";
import Button from "../../../ui/buttons";
import ScreenshotManager from "../../../ui/screenshot-screenrecord/Screenshot/ScreenshotManager";
import { ScreenshotSubmission } from "../../../ui/screenshot-screenrecord/Screenshot/Screenshot_Preview";
import ScreenshotPreviewComponent from "../../../ui/screenshot-screenrecord/Screenshot/Screenshot_Preview";
import ScreenRecordingPreview, {
  RecordingSubmission,
} from "../../../ui/screenshot-screenrecord/screen-recording/ScreenRecordingPreview";
import { API_URLS } from "../../../../api/urls";
import { EnhancedTokenManager } from "../../../../utils/tokenManager";
import MinimizedWorkspace from "../../../ui/MinimizedWorkspace";

declare global {
  interface Window {
    __emptyContainerRef?: HTMLElement;
  }
}

interface UplaodFeedbackWorkspaceProps {
  feedbackName: string;
  onBack: (deletionState?: {
    cardDeleted: boolean;
    deletedCardId: number;
    deletedCardName: string;
    activeTab: "draft";
  }) => void;
  onSaveAndExit: (
    orderedFeedbackItems: FeedbackItem[],
    workspaceState?: {
      screenshots: ScreenshotSubmissionEnhanced[];
      recordings: RecordingSubmissionEnhanced[];
      feedbackItemsOrder: string[];
      feedbackId?: string;
      isUrlLocked?: boolean;
    }
  ) => void;
  onSaveDraft: (
    orderedFeedbackItems: FeedbackItem[],
    workspaceState?: {
      screenshots: ScreenshotSubmissionEnhanced[];
      recordings: RecordingSubmissionEnhanced[];
      feedbackItemsOrder: string[];
      feedbackId?: string;
      isUrlLocked?: boolean;
    }
  ) => void;
  onFirstScreenshotTaken?: (screenshotImage: string) => void;
  onMinimize?: (
    liveFeedbackItems: FeedbackItem[],
    liveWorkspaceState: {
      screenshots: ScreenshotSubmissionEnhanced[];
      recordings: RecordingSubmissionEnhanced[];
      feedbackItemsOrder: string[];
      feedbackId?: string;
      isUrlLocked?: boolean;
    }
  ) => void;
  cardId: number;
  workspaceId: number;
  initialWorkspaceState?: {
    screenshots: ScreenshotSubmissionEnhanced[];
    recordings: RecordingSubmissionEnhanced[];
    feedbackItemsOrder: string[];
    feedbackId?: string;
    isUrlLocked?: boolean;
  } | null;
  initialFeedbackItems?: FeedbackItem[];
}

interface FeedbackItem {
  id: string;
  type: "screenshot" | "recording";
  timestamp: number;
  order: number;
  annotatedImage?: string;
  originalImage?: string;
  dataUrl?: string;
  recordingBlob?: Blob;
  duration?: number;
  hasAudio?: boolean;
  audioUrl?: string;
  audioFilename?: string;
  feedback?: string;
  question?: string;
  user?: {
    name: string;
    avatar: string;
  };
  isSelected?: boolean;
  uniqueFeedbackId?: string;
  thumbnailUrl?: string;
}

interface ScreenshotSubmissionEnhanced extends ScreenshotSubmission {
  uniqueFeedbackId?: string;
  question?: string;
  user?: {
    name: string;
    avatar: string;
  };
  isSelected?: boolean;
}

interface RecordingSubmissionEnhanced extends RecordingSubmission {
  uniqueFeedbackId?: string;
  question?: string;
  user?: {
    name: string;
    avatar: string;
  };
  isSelected?: boolean;
  thumbnailUrl?: string;
}

interface CreateFeedbackApiResponse {
  id: string;
  name: string;
  url: string;
  created_at: string;
}

// Added to fix TypeScript error on line 927
interface FeedbackData {
  name: string;
  collaborators: string[];
  accessType: "comment" | "edit" | "view";
}

// Added to fix TypeScript error on line 928
interface CreateCardApiResponse {
  id: string;
  name: string;
  created_at: string;
}

//  Added to fix TypeScript error on line 929
interface WorkspaceState {
  screenshots: ScreenshotSubmissionEnhanced[];
  recordings: RecordingSubmissionEnhanced[];
  feedbackItemsOrder: string[];
  feedbackId?: string;
  isUrlLocked?: boolean;
}

interface RefreshTokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type?: string;
}

interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

class ApiService {
  private static isRefreshing = false;
  private static failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (reason: any) => void;
  }> = [];

  private static processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    this.failedQueue = [];
  }

  static async refreshAccessToken(): Promise<boolean> {
    const refreshToken = EnhancedTokenManager.getRefreshToken();

    if (!refreshToken) {
      console.log("No refresh token available");
      return false;
    }

    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      console.log("Attempting to refresh access token...");
      const response = await fetch(API_URLS.REFRESH_TOKEN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      });

      console.log("Refresh token response status:", response.status);

      if (response.ok) {
        const data: RefreshTokenResponse = await response.json();
        console.log("Token refresh successful");

        const newTokens: AuthTokens = {
          access_token: data.access_token,
          refresh_token: data.refresh_token || refreshToken,
          token_type: data.token_type || "bearer",
        };

        EnhancedTokenManager.setTokens(newTokens);
        this.processQueue(null, data.access_token);
        return true;
      } else {
        const errorData = await response.json();
        console.error("Refresh token failed:", errorData);
        EnhancedTokenManager.performLogout();
        this.processQueue(errorData, null);
        return false;
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      this.processQueue(error, null);
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  static async makeAuthenticatedRequest(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const accessToken = EnhancedTokenManager.getAccessToken();

    if (!accessToken) {
      throw new Error("No access token available. Please log in again.");
    }

    console.log(`Making authenticated request to: ${url}`);

    const isFormData = options.body instanceof FormData;

    const headers = {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    };

    let response = await fetch(url, {
      ...options,
      headers,
    });

    console.log(`Initial response status: ${response.status}`);

    if (response.status === 401) {
      console.log("Received 401, attempting token refresh...");

      const refreshed = await this.refreshAccessToken();

      if (refreshed) {
        const newAccessToken = EnhancedTokenManager.getAccessToken();
        console.log(
          "Token refreshed successfully, retrying original request..."
        );

        response = await fetch(url, {
          ...options,
          headers: {
            ...headers,
            Authorization: `Bearer ${newAccessToken}`,
          },
        });

        console.log(`Retry response status: ${response.status}`);
      } else {
        throw new Error("Authentication failed. Please log in again.");
      }
    }

    return response;
  }

  static async createFeedbackInsideCard(
    cardId: number,
    workspaceId: number,
    url: string,
    name?: string
  ): Promise<CreateFeedbackApiResponse | null> {
    try {
      const apiUrl = API_URLS.CREATE_FEEDBACK_INSIDE_CARD(cardId, workspaceId);

      console.log("🚀 Creating feedback inside card:", {
        cardId,
        workspaceId,
        url,
        name,
        apiUrl,
      });

      const response = await this.makeAuthenticatedRequest(apiUrl, {
        method: "POST",
        body: JSON.stringify([
          {
            url: url,
            name: name || `Feedback for ${url}`,
            workspace_id: workspaceId,
          },
        ]),
      });

      console.log("🚀 Create feedback response status:", response.status);

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = `HTTP ${response.status}: ${errorText}`;
          }
          console.error("🚨 API Error Response:", errorText);
        } catch (e) {}

        if (response.status === 401) {
          errorMessage =
            "Authentication failed. Please check your access token.";
        } else if (response.status === 403) {
          errorMessage =
            "Access denied. You do not have permission to create feedback.";
        } else if (response.status === 404) {
          errorMessage = "Card not found. Please check the card ID.";
        } else if (response.status === 422) {
          errorMessage =
            "Invalid request data. Please check the required fields.";
        } else if (response.status >= 500) {
          errorMessage = "Server error. Please try again later.";
        }

        throw new Error(errorMessage);
      }

      const apiResponseArray: CreateFeedbackApiResponse[] =
        await response.json();
      console.log("✅ Feedback created successfully:", apiResponseArray);

      const feedbackObject = apiResponseArray[0];

      if (!feedbackObject || !feedbackObject.id) {
        throw new Error("Invalid response: No feedback object or ID found");
      }

      console.log("✅ Extracted feedback ID:", feedbackObject.id);
      return feedbackObject;
    } catch (error) {
      console.error("🚨 Error creating feedback:", error);
      throw error;
    }
  }

  static async createFeedbackForItem(
    cardId: number,
    workspaceId: number,
    url: string,
    itemType: "screenshot" | "recording",
    itemId: string,
    preservedName?: string
  ): Promise<CreateFeedbackApiResponse | null> {
    const name =
      preservedName ||
      `${
        itemType.charAt(0).toUpperCase() + itemType.slice(1)
      } Feedback - ${itemId}`;

    try {
      return await this.createFeedbackInsideCard(
        cardId,
        workspaceId,
        url,
        name
      );
    } catch (error) {
      console.error(`🚨 Error creating feedback for ${itemType}:`, error);
      throw error;
    }
  }

  static async uploadMessage(
    message: string,
    feedbackId: string | number
  ): Promise<any> {
    try {
      const cleanFeedbackId = String(feedbackId).trim();

      console.log("💬 Uploading message:", {
        messageLength: message.length,
        feedbackId: cleanFeedbackId,
        messagePreview:
          message.substring(0, 100) + (message.length > 100 ? "..." : ""),
      });

      const formData = new FormData();
      formData.append("message", message);
      formData.append("feedback_id", cleanFeedbackId);

      console.log("💬 FormData contents:");
      const entries = Array.from(formData.entries());
      entries.forEach(([key, value]) => {
        console.log(`  ${key}: ${value}`);
      });

      const response = await this.makeAuthenticatedRequest(
        API_URLS.UPLOAD_MESSAGE,
        {
          method: "POST",
          body: formData,
          headers: {},
        }
      );

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = `HTTP ${response.status}: ${errorText}`;
          }
        } catch (e) {}
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("✅ Message uploaded successfully:", result);
      return result;
    } catch (error) {
      console.error("🚨 Error uploading message:", error);
      throw error;
    }
  }

  static async uploadVideoThumbnail(
    thumbnailDataUrl: string,
    feedbackId: string | number
  ): Promise<any> {
    try {
      const cleanFeedbackId = String(feedbackId).trim();

      console.log("🖼️ Uploading video thumbnail:", {
        feedbackId: cleanFeedbackId,
        thumbnailSize: thumbnailDataUrl.length,
      });

      const response = await fetch(thumbnailDataUrl);
      const blob = await response.blob();

      const pngBlob = new Blob([blob], { type: "image/png" });

      const formData = new FormData();
      const fileName = `thumbnail_${cleanFeedbackId}_${Date.now()}.png`;
      formData.append("file", pngBlob, fileName);
      formData.append("feedback_id", cleanFeedbackId);

      console.log("🖼️ Thumbnail FormData contents:");
      const entries = Array.from(formData.entries());
      entries.forEach(([key, value]) => {
        if (value instanceof File) {
          console.log(
            `  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`
          );
        } else {
          console.log(`  ${key}: ${value}`);
        }
      });

      const uploadResponse = await this.makeAuthenticatedRequest(
        API_URLS.UPLOAD_VIDEO_THUMBNAIL(cleanFeedbackId),
        {
          method: "POST",
          body: formData,
          headers: {},
        }
      );

      if (!uploadResponse.ok) {
        let errorMessage = `HTTP ${uploadResponse.status}: ${uploadResponse.statusText}`;
        try {
          const errorText = await uploadResponse.text();
          if (errorText) {
            errorMessage = `HTTP ${uploadResponse.status}: ${errorText}`;
          }
        } catch (e) {}
        throw new Error(errorMessage);
      }

      const result = await uploadResponse.json();
      console.log("✅ Video thumbnail uploaded successfully:", result);
      return result;
    } catch (error) {
      console.error("🚨 Error uploading video thumbnail:", error);
      throw error;
    }
  }

  static async uploadFile(
    file: File | Blob,
    fileName: string,
    fileType: "screenshot" | "recording" | "audio",
    feedbackId?: string
  ): Promise<any> {
    try {
      if (!file) {
        throw new Error("No file provided for upload");
      }

      if (file.size === 0) {
        throw new Error("File is empty");
      }

      const maxSize =
        fileType === "recording" ? 100 * 1024 * 1024 : 50 * 1024 * 1024;
      if (file.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        throw new Error(`File size exceeds ${maxSizeMB}MB limit`);
      }

      const formData = new FormData();

      if (fileType === "screenshot") {
        const ensuredFileName = fileName.endsWith(".png")
          ? fileName
          : `${fileName}.png`;

        let processedBlob: Blob = file;

        if (file.type !== "image/png") {
          try {
            const img = new Image();
            const imageUrl = URL.createObjectURL(file);

            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
              img.src = imageUrl;
            });

            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext("2d");

            if (ctx) {
              ctx.drawImage(img, 0, 0);

              processedBlob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(
                  (blob) => {
                    if (blob) resolve(blob);
                    else reject(new Error("PNG conversion failed"));
                  },
                  "image/png",
                  1.0
                );
              });
            }

            URL.revokeObjectURL(imageUrl);
          } catch (convErr) {
            console.warn("PNG conversion failed:", convErr);
            processedBlob = new Blob([file], { type: "image/png" });
          }
        }

        const pngFile = new File([processedBlob], ensuredFileName, {
          type: "image/png",
          lastModified: Date.now(),
        });
        formData.append("file", pngFile, ensuredFileName);

        console.log("🖼️ Screenshot file prepared:", {
          fileName: ensuredFileName,
          size: pngFile.size,
          type: pngFile.type,
        });
      } else if (fileType === "audio") {
        const audioFile = new File([file], fileName, {
          type: file.type || "audio/mp3",
          lastModified: Date.now(),
        });
        formData.append("file", audioFile, fileName);
      } else if (fileType === "recording") {
        let finalFile: File;
        if (file instanceof File) {
          finalFile = file;
        } else {
          const videoType = fileName.endsWith(".webm")
            ? "video/webm"
            : "video/mp4";
          finalFile = new File([file], fileName, {
            type: videoType,
            lastModified: Date.now(),
          });
        }
        formData.append("file", finalFile);

        console.log("📹 Video file prepared:", {
          fileName: finalFile.name,
          size: finalFile.size,
          type: finalFile.type,
        });
      }

      formData.append("file_type", fileType);

      if (feedbackId) {
        const cleanFeedbackId = String(feedbackId).trim();
        formData.append("feedback_id", cleanFeedbackId);
        console.log("🚀 Adding feedback_id to FormData:", cleanFeedbackId);
      }

      const apiUrl =
        fileType === "audio"
          ? API_URLS.UPLOAD_FILE_VOICE
          : API_URLS.UPLOAD_FILE;

      console.log("🚀 Uploading file:", {
        fileName,
        fileType,
        fileSize: file.size,
        endpoint: apiUrl,
        feedbackId: feedbackId || "none",
      });

      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available");
      }

      const timeoutDuration = fileType === "recording" ? 300000 : 60000;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);

      try {
        const fetchOptions: RequestInit = {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: controller.signal,
          mode: "cors",
          credentials: "omit",
          cache: "no-cache",
        };

        const response = await fetch(apiUrl, fetchOptions);

        clearTimeout(timeoutId);
        console.log("🚀 Upload response status:", response.status);

        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          let responseText = "";

          try {
            responseText = await response.text();
            console.error("🚨 Upload Error Response:", responseText);

            if (response.status === 0 || response.type === "opaque") {
              throw new Error(
                "CORS error: Cannot reach upload server. Please check:\n" +
                  "1. Server CORS configuration allows this origin\n" +
                  "2. Server is running and accessible\n" +
                  "3. Network connection is stable\n" +
                  "4. No browser extensions blocking requests"
              );
            }

            try {
              const errorJson = JSON.parse(responseText);
              if (errorJson.detail) {
                errorMessage = errorJson.detail;
              } else if (errorJson.message) {
                errorMessage = errorJson.message;
              } else if (errorJson.error) {
                errorMessage = errorJson.error;
              }
            } catch (parseError) {
              if (responseText && responseText.length > 0) {
                errorMessage = responseText.substring(0, 200);
              }
            }
          } catch (textError) {
            console.warn("Could not read error response:", textError);
          }

          if (response.status === 413) {
            throw new Error(
              `File too large (${(file.size / (1024 * 1024)).toFixed(2)}MB). ` +
                `Maximum size for ${fileType}: ${maxSize / (1024 * 1024)}MB`
            );
          } else if (response.status === 415) {
            throw new Error(`Unsupported file type for ${fileType}.`);
          } else if (response.status === 401) {
            throw new Error(
              "Authentication failed. Please refresh and try again."
            );
          } else if (response.status === 403) {
            throw new Error(
              "Permission denied. You don't have access to upload files."
            );
          } else if (response.status === 429) {
            throw new Error(
              "Too many requests. Please wait a moment and try again."
            );
          } else if (response.status >= 500) {
            throw new Error(
              `Server error (${response.status}). Please try again later.`
            );
          } else {
            throw new Error(errorMessage);
          }
        }

        let result;
        const contentType = response.headers.get("content-type");

        if (contentType && contentType.includes("application/json")) {
          result = await response.json();
        } else {
          const textResponse = await response.text();
          try {
            result = JSON.parse(textResponse);
          } catch (parseError) {
            result = { message: textResponse, status: "uploaded" };
          }
        }

        console.log("✅ File uploaded successfully:", result);
        return result;
      } catch (uploadError) {
        clearTimeout(timeoutId);

        if ((uploadError as any).name === "AbortError") {
          const timeoutMessage =
            fileType === "recording"
              ? `Video upload timed out after ${
                  timeoutDuration / 1000
                } seconds. File may be too large or connection too slow.`
              : "Upload timed out. Please try again.";
          throw new Error(timeoutMessage);
        }

        if (uploadError instanceof TypeError) {
          const errorMessage = (uploadError as Error).message.toLowerCase();

          if (
            errorMessage.includes("cors") ||
            errorMessage.includes("cross-origin")
          ) {
            throw new Error(
              "CORS error: Server doesn't allow uploads from this domain. " +
                "This is a server configuration issue. Please contact support."
            );
          } else if (
            errorMessage.includes("fetch") ||
            errorMessage.includes("network") ||
            errorMessage.includes("failed to fetch")
          ) {
            throw new Error(
              "Network error: Cannot reach upload server. Please check your internet connection and try again."
            );
          }
        }

        throw uploadError;
      }
    } catch (error) {
      console.error("🚨 Error uploading file:", error);

      const errorMessage = (error as Error).message;

      if (
        errorMessage.includes("Failed to fetch") ||
        errorMessage.includes("NetworkError")
      ) {
        throw new Error(
          "Network connection failed. Please check your internet connection and try again."
        );
      } else if (errorMessage.includes("CORS")) {
        throw new Error(
          "Server configuration error (CORS). Please contact support with error code: CORS_001"
        );
      } else if (
        errorMessage.includes("413") ||
        errorMessage.includes("too large")
      ) {
        throw new Error(
          `File too large (${(file.size / (1024 * 1024)).toFixed(
            2
          )}MB). Please reduce file size and try again.`
        );
      } else if (
        errorMessage.includes("415") ||
        errorMessage.includes("Unsupported")
      ) {
        throw new Error(`File format not supported for ${fileType}.`);
      } else if (
        errorMessage.includes("401") ||
        errorMessage.includes("Authentication")
      ) {
        throw new Error(
          "Authentication expired. Please refresh the page and try again."
        );
      } else if (
        errorMessage.includes("403") ||
        errorMessage.includes("Permission")
      ) {
        throw new Error(
          "Permission denied. You don't have access to upload files."
        );
      } else if (
        errorMessage.includes("timeout") ||
        errorMessage.includes("504")
      ) {
        throw new Error(
          "Upload timed out. File may be too large or connection too slow."
        );
      }

      throw error;
    }
  }
}

const UplaodFeedbackWorkspace: React.FC<UplaodFeedbackWorkspaceProps> = ({
  feedbackName,
  onBack,
  onSaveAndExit,
  onSaveDraft,
  onFirstScreenshotTaken,
  onMinimize,
  cardId,
  workspaceId,
  initialWorkspaceState,
  initialFeedbackItems,
}) => {
  const [feedbackId, setFeedbackId] = useState<string | undefined>(
    initialWorkspaceState?.feedbackId || undefined
  );
  const [screenshots, setScreenshots] = useState<
    ScreenshotSubmissionEnhanced[]
  >(initialWorkspaceState?.screenshots || []);
  const [recordings, setRecordings] = useState<RecordingSubmissionEnhanced[]>(
    initialWorkspaceState?.recordings || []
  );

  const [isCreatingFeedback, setIsCreatingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string>("");

  const [feedbackItemsOrder, setFeedbackItemsOrder] = useState<string[]>(
    initialWorkspaceState?.feedbackItemsOrder || []
  );

  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

  const [loadingItems, setLoadingItems] = useState<
    {
      id: string;
      type: "screenshot" | "recording";
      timestamp: number;
      progress: number;
    }[]
  >([]);

  const [editingScreenshot, setEditingScreenshot] =
    useState<ScreenshotSubmissionEnhanced | null>(null);
  const [showEditPreview, setShowEditPreview] = useState(false);

  const [originalFeedbackName, setOriginalFeedbackName] = useState<string>(
    initialWorkspaceState?.feedbackId ? feedbackName : ""
  );

  const [isFirstScreenshot, setIsFirstScreenshot] = useState(true);

  const [isUrlLocked, setIsUrlLocked] = useState(
    initialWorkspaceState?.isUrlLocked ||
      (initialFeedbackItems && initialFeedbackItems.length > 0) ||
      false
  );

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    id: string;
    type: "screenshot" | "recording";
    uniqueFeedbackId?: string;
  } | null>(null);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");

  const [hasAddedFeedback, setHasAddedFeedback] = useState(
    (initialFeedbackItems && initialFeedbackItems.length > 0) || false
  );

  const sidebarItemsRef = useRef<HTMLDivElement | null>(null);

  const prevFeedbackCountRef = useRef(0);

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isMuted, setIsMuted] = useState(() => {
    const savedMuteState = localStorage.getItem("screenshot_sound_muted");
    return savedMuteState === "true";
  });

  const [showDeleteCardConfirm, setShowDeleteCardConfirm] = useState(false);
  const [isDeletingCard, setIsDeletingCard] = useState(false);

  const [editingRecording, setEditingRecording] =
    useState<RecordingSubmissionEnhanced | null>(null);
  const [showEditRecordingPreview, setShowEditRecordingPreview] =
    useState(false);

  // Picture-in-Picture state
  const [isPiPMode, setIsPiPMode] = useState(false);
  const [pipWorkspaceData, setPipWorkspaceData] = useState<{
    feedbackName: string;
    previewImage: string;
    feedbackData: FeedbackData;
    apiResponse: CreateCardApiResponse;
    workspaceState: WorkspaceState | null;
    feedbackItems: FeedbackItem[];
  } | null>(null);

  //helper function to generate placeholder images
  const generatePlaceholderImage = (feedbackName: string): string => {
    return `https://via.placeholder.com/400x300/6B87E6/FFFFFF?text=${encodeURIComponent(
      feedbackName.substring(0, 20) + (feedbackName.length > 20 ? "..." : "")
    )}`;
  };

  useEffect(() => {
    if (initialFeedbackItems && initialFeedbackItems.length > 0) {
      console.log("🔄 UplaodFeedbackWorkspace - Restoring feedback items:", {
        totalItems: initialFeedbackItems.length,
        receivedOrder: initialFeedbackItems.map((item, idx) => ({
          position: idx + 1,
          type: item.type,
          id: item.id,
          timestamp: new Date(item.timestamp).toISOString(),
        })),
      });

      const restoredScreenshots: ScreenshotSubmissionEnhanced[] = [];
      const restoredRecordings: RecordingSubmissionEnhanced[] = [];
      const restoredOrder: string[] = [];

      initialFeedbackItems.forEach((item) => {
        const itemKey = `${item.type}-${item.id}`;
        restoredOrder.push(itemKey);

        if (item.type === "screenshot") {
          const screenshot: ScreenshotSubmissionEnhanced = {
            id: item.id,
            annotatedImage: item.annotatedImage || "",
            originalImage: item.originalImage || "",
            feedback: item.feedback || "",
            timestamp: item.timestamp,
            hasAudio: item.hasAudio || false,
            audioUrl: item.audioUrl,
            audioFilename: item.audioFilename,
            question: item.question,
            user: item.user,
            isSelected: item.isSelected || false,
            uniqueFeedbackId: item.uniqueFeedbackId || item.id,
          };
          restoredScreenshots.push(screenshot);
        } else if (item.type === "recording") {
          let recordingBlob = item.recordingBlob;

          if (!recordingBlob && item.dataUrl) {
            fetch(item.dataUrl)
              .then((response) => response.blob())
              .then((blob) => {
                console.log(
                  `📹 Restored recording blob for ${item.id}:`,
                  blob.size,
                  "bytes"
                );
                setRecordings((prevRecordings) =>
                  prevRecordings.map((recording) =>
                    recording.id === item.id
                      ? { ...recording, recordingBlob: blob }
                      : recording
                  )
                );
              })
              .catch((error) => {
                console.warn(
                  `Failed to restore recording blob for ${item.id}:`,
                  error
                );
              });

            recordingBlob = new Blob([], { type: "video/mp4" });
          } else if (!recordingBlob) {
            recordingBlob = new Blob([], { type: "video/mp4" });
          }

          const recording: RecordingSubmissionEnhanced = {
            id: item.id,
            dataUrl: item.dataUrl || "",
            recordingBlob: recordingBlob,
            duration: item.duration || 0,
            feedback: item.feedback || "",
            timestamp: item.timestamp,
            hasAudio: item.hasAudio || false,
            question: item.question,
            user: item.user,
            isSelected: item.isSelected || false,
            uniqueFeedbackId: item.uniqueFeedbackId || item.id,
            thumbnailUrl: item.thumbnailUrl,
          };
          restoredRecordings.push(recording);
        }
      });

      console.log("✅ Restoring items with order:", {
        restoredOrder: restoredOrder,
        screenshots: restoredScreenshots.length,
        recordings: restoredRecordings.length,
      });

      setFeedbackItemsOrder(restoredOrder);
      setScreenshots(restoredScreenshots);
      setRecordings(restoredRecordings);

      console.log("✅ State updated - items restored");
    }
  }, [initialFeedbackItems]);

  useEffect(() => {
    if (initialWorkspaceState) {
      console.log("🔄 Restoring workspace state:", {
        ...initialWorkspaceState,
        screenshotsCount: initialWorkspaceState.screenshots?.length || 0,
        recordingsCount: initialWorkspaceState.recordings?.length || 0,
      });

      if (initialWorkspaceState.feedbackId) {
        setFeedbackId(initialWorkspaceState.feedbackId);
      }

      if (typeof initialWorkspaceState.isUrlLocked === "boolean") {
        setIsUrlLocked(initialWorkspaceState.isUrlLocked);
        console.log(
          "🔒 URL lock state restored:",
          initialWorkspaceState.isUrlLocked
        );
      } else {
        const hasItems =
          (initialWorkspaceState.screenshots?.length || 0) > 0 ||
          (initialWorkspaceState.recordings?.length || 0) > 0;
        if (hasItems) {
          setIsUrlLocked(true);
          console.log("🔒 URL locked due to existing feedback items");
        }
      }

      if (initialWorkspaceState.feedbackItemsOrder) {
        setFeedbackItemsOrder(initialWorkspaceState.feedbackItemsOrder);
      }
    }
  }, [initialWorkspaceState]);

  useEffect(() => {
    console.log("🆔 Feedback ID changed:", feedbackId);
  }, [feedbackId]);

  const allFeedbackItems = useMemo(() => {
    console.log("🔄 Computing allFeedbackItems:", {
      feedbackItemsOrderLength: feedbackItemsOrder.length,
      screenshotsCount: screenshots.length,
      recordingsCount: recordings.length,
      orderArray: feedbackItemsOrder,
    });

    if (feedbackItemsOrder.length > 0) {
      const screenshotMap = new Map(
        screenshots.map((item) => [`screenshot-${item.id}`, item])
      );
      const recordingMap = new Map(
        recordings.map((item) => [`recording-${item.id}`, item])
      );

      const orderedItems: FeedbackItem[] = [];

      feedbackItemsOrder.forEach((itemKey, index) => {
        if (itemKey.startsWith("screenshot-")) {
          const screenshot = screenshotMap.get(itemKey);
          if (screenshot) {
            orderedItems.push({
              id: screenshot.id,
              type: "screenshot" as const,
              timestamp: screenshot.timestamp,
              order: index,
              annotatedImage: screenshot.annotatedImage,
              originalImage: screenshot.originalImage,
              feedback: screenshot.feedback,
              hasAudio: screenshot.hasAudio || false,
              audioUrl: screenshot.audioUrl,
              audioFilename: screenshot.audioFilename,
              question: screenshot.question,
              user: screenshot.user,
              isSelected: screenshot.isSelected || false,
              uniqueFeedbackId: screenshot.uniqueFeedbackId || screenshot.id,
            });
          }
        } else if (itemKey.startsWith("recording-")) {
          const recording = recordingMap.get(itemKey);
          if (recording) {
            orderedItems.push({
              id: recording.id,
              type: "recording" as const,
              timestamp: recording.timestamp,
              order: index,
              dataUrl: recording.dataUrl,
              recordingBlob: recording.recordingBlob,
              duration: recording.duration,
              feedback: recording.feedback,
              hasAudio: false,
              audioUrl: undefined,
              audioFilename: undefined,
              question: recording.question,
              user: recording.user,
              isSelected: recording.isSelected || false,
              uniqueFeedbackId: recording.uniqueFeedbackId || recording.id,
              thumbnailUrl: recording.thumbnailUrl,
            });
          }
        }
      });

      console.log("✅ allFeedbackItems built from order array:", {
        totalItems: orderedItems.length,
        sequence: orderedItems.map((item, idx) => ({
          position: idx + 1,
          type: item.type,
          id: item.id,
        })),
      });

      return orderedItems;
    }

    const combined: FeedbackItem[] = [
      ...screenshots.map((item) => ({
        ...item,
        type: "screenshot" as const,
        order: -1,
        hasAudio: item.hasAudio || false,
        audioUrl: item.audioUrl,
        audioFilename: item.audioFilename,
        uniqueFeedbackId: item.uniqueFeedbackId || item.id,
      })),
      ...recordings.map((item) => ({
        ...item,
        type: "recording" as const,
        order: -1,
        hasAudio: false,
        audioUrl: undefined,
        audioFilename: undefined,
        recordingBlob: item.recordingBlob,
        uniqueFeedbackId: item.uniqueFeedbackId || item.id,
        thumbnailUrl: item.thumbnailUrl,
      })),
    ];

    const sorted = combined.sort((a, b) => a.timestamp - b.timestamp);

    console.log("✅ allFeedbackItems sorted by timestamp (fallback):", {
      totalItems: sorted.length,
      sequence: sorted.map((item, idx) => ({
        position: idx + 1,
        type: item.type,
        id: item.id,
      })),
    });

    return sorted;
  }, [screenshots, recordings, feedbackItemsOrder]);

  const totalFeedbackItems = allFeedbackItems.length;

  useEffect(() => {
    const currentCount = allFeedbackItems.length + loadingItems.length;

    if (
      currentCount > prevFeedbackCountRef.current &&
      sidebarItemsRef.current
    ) {
      sidebarItemsRef.current.scrollTo({
        top: sidebarItemsRef.current.scrollHeight,
        behavior: "smooth",
      });

      console.log("📜 Auto-scrolling sidebar to show new feedback item", {
        previousCount: prevFeedbackCountRef.current,
        currentCount,
        scrollHeight: sidebarItemsRef.current.scrollHeight,
      });
    }

    prevFeedbackCountRef.current = currentCount;
  }, [allFeedbackItems.length, loadingItems.length]);

  useEffect(() => {
    if (totalFeedbackItems > 0 && !isUrlLocked) {
      setIsUrlLocked(true);
      console.log("🔒 URL locked after first feedback item added");
    }
  }, [totalFeedbackItems, isUrlLocked]);

  useEffect(() => {
    const currentIds = new Set(feedbackItemsOrder);
    const newItems: string[] = [];

    screenshots.forEach((item) => {
      const id = `screenshot-${item.id}`;
      if (!currentIds.has(id)) {
        newItems.push(id);
      }
    });

    recordings.forEach((item) => {
      const id = `recording-${item.id}`;
      if (!currentIds.has(id)) {
        newItems.push(id);
      }
    });

    if (newItems.length > 0) {
      setFeedbackItemsOrder((prev) => [...prev, ...newItems]);
    }
  }, [screenshots, recordings, feedbackItemsOrder]);

  useEffect(() => {
    const validIds = new Set([
      ...screenshots.map((item) => `screenshot-${item.id}`),
      ...recordings.map((item) => `recording-${item.id}`),
    ]);

    setFeedbackItemsOrder((prev) => prev.filter((id) => validIds.has(id)));
  }, [screenshots, recordings]);

  useEffect(() => {
    return () => {
      screenshots.forEach((screenshot) => {
        if (screenshot.audioUrl && screenshot.audioUrl.startsWith("blob:")) {
          URL.revokeObjectURL(screenshot.audioUrl);
        }
      });
      recordings.forEach((recording) => {
        if (recording.dataUrl && recording.dataUrl.startsWith("blob:")) {
          URL.revokeObjectURL(recording.dataUrl);
        }
      });
    };
  }, [screenshots, recordings]);

  useEffect(() => {
    if (loadingItems.length === 0) return;

    const interval = setInterval(() => {
      setLoadingItems((prev) =>
        prev.map((item) => {
          if (item.progress < 80) {
            return {
              ...item,
              progress: Math.min(item.progress + 2, 80),
            };
          }
          return item;
        })
      );
    }, 100);

    return () => clearInterval(interval);
  }, [loadingItems.length]);

  useEffect(() => {
    if (!(window as any).__audio_cache) {
      (window as any).__audio_cache = {};
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isTyping =
        target.tagName === "INPUT" || target.tagName === "TEXTAREA";

      if ((event.key === "h" || event.key === "H") && !isTyping) {
        event.preventDefault();
        handleGoToHome();
      }

      if ((event.key === "m" || event.key === "M") && !isTyping) {
        event.preventDefault();
        handleToggleMute();
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("screenshot_sound_muted", String(isMuted));
  }, [isMuted]);

  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        return;
      }

      if (event.key.toLowerCase() === "s") {
        event.preventDefault();
        event.stopPropagation();
        window.dispatchEvent(new CustomEvent("trigger-screenshot"));
      } else if (event.key.toLowerCase() === "r") {
        event.preventDefault();
        event.stopPropagation();
        window.dispatchEvent(new CustomEvent("trigger-recording"));
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown, true);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (
        editingScreenshot?.audioUrl &&
        editingScreenshot.audioUrl.startsWith("blob:")
      ) {
        URL.revokeObjectURL(editingScreenshot.audioUrl);
        console.log("🧹 Cleaned up editing screenshot blob URL");
      }
    };
  }, [editingScreenshot?.audioUrl]);

  // Update the handleMinimizeWorkspace callback (around line 450):
  const handleMinimizeWorkspace = useCallback(
    (
      liveFeedbackItems: FeedbackItem[],
      liveWorkspaceState: {
        screenshots: ScreenshotSubmissionEnhanced[];
        recordings: RecordingSubmissionEnhanced[];
        feedbackItemsOrder: string[];
        feedbackId?: string;
        isUrlLocked?: boolean;
      }
    ) => {
      if (!feedbackName) {
        console.warn("Cannot minimize: missing feedback data");
        return;
      }

      const feedbackItemsToUse =
        liveFeedbackItems.length > 0 ? liveFeedbackItems : allFeedbackItems;

      // Get preview image from first screenshot or generate placeholder
      const firstScreenshot = feedbackItemsToUse.find(
        (item) => item.type === "screenshot"
      );
      const previewImage =
        firstScreenshot?.annotatedImage ||
        generatePlaceholderImage(feedbackName);

      console.log("📦 Minimizing upload workspace with live data:", {
        liveFeedbackCount: liveFeedbackItems.length,
        stateFeedbackCount: allFeedbackItems.length,
        usingLiveData: liveFeedbackItems.length > 0,
        finalCount: feedbackItemsToUse.length,
      });

      // Store all workspace data with LIVE feedback items
      setPipWorkspaceData({
        feedbackName,
        previewImage,
        feedbackData: {
          name: feedbackName,
          collaborators: [],
          accessType: "comment" as const,
        },
        apiResponse: {
          id: cardId.toString(),
          name: feedbackName,
          created_at: new Date().toISOString(),
        },
        workspaceState: liveWorkspaceState,
        feedbackItems: feedbackItemsToUse,
      });

      // Switch to PiP mode
      setIsPiPMode(true);

      console.log("✅ Upload workspace minimized to PiP mode:", {
        feedbackName,
        feedbackCount: feedbackItemsToUse.length,
        hasPreviewImage: !!previewImage,
      });
    },
    [feedbackName, allFeedbackItems, cardId, generatePlaceholderImage]
  );

  // Add handleMaximizeFromPiP callback (around line 520):
  const handleMaximizeFromPiP = useCallback(() => {
    if (!pipWorkspaceData) {
      console.warn("Cannot maximize: no PiP data available");
      return;
    }

    console.log("📤 Maximizing from PiP with data:", {
      feedbackCount: pipWorkspaceData.feedbackItems.length,
      feedbackItems: pipWorkspaceData.feedbackItems.map((item) => ({
        id: item.id,
        type: item.type,
        hasUniqueId: !!item.uniqueFeedbackId,
      })),
    });

    // Restore all workspace state with ALL feedback items
    if (pipWorkspaceData.workspaceState) {
      setScreenshots(pipWorkspaceData.workspaceState.screenshots);
      setRecordings(pipWorkspaceData.workspaceState.recordings);
      setFeedbackItemsOrder(pipWorkspaceData.workspaceState.feedbackItemsOrder);
      setFeedbackId(pipWorkspaceData.workspaceState.feedbackId);
      setIsUrlLocked(pipWorkspaceData.workspaceState.isUrlLocked || false);
    }

    // Switch back to full workspace view
    setIsPiPMode(false);
    setPipWorkspaceData(null);

    console.log("✅ Upload workspace maximized from PiP mode:", {
      feedbackName: pipWorkspaceData.feedbackName,
      feedbackCount: pipWorkspaceData.feedbackItems.length,
      restoredItems: pipWorkspaceData.feedbackItems.length,
    });
  }, [pipWorkspaceData]);

  // Update the handleGoToHome function to pass live data (around line 1200):
  const handleGoToHome = useCallback(() => {
    setShowDropdown(false);
    if (onMinimize) {
      console.log(
        "🔽 Minimizing upload workspace to PiP mode with live data:",
        {
          feedbackCount: allFeedbackItems.length,
          screenshots: screenshots.length,
          recordings: recordings.length,
        }
      );

      const liveWorkspaceState = {
        screenshots,
        recordings,
        feedbackItemsOrder,
        feedbackId,
        isUrlLocked,
      };

      onMinimize(allFeedbackItems, liveWorkspaceState);
    }
  }, [
    onMinimize,
    allFeedbackItems,
    screenshots,
    recordings,
    feedbackItemsOrder,
    feedbackId,
    isUrlLocked,
  ]);

  const processScreenshotUpload = useCallback(
    async (
      submission: ScreenshotSubmission,
      skipLoadingState: boolean = false
    ) => {
      console.log("📤 Processing screenshot upload:", {
        id: submission.id,
        hasAudio: submission.hasAudio,
        hasStartRect: !!(submission as any).startRect,
        skipLoadingState,
      });

      if (!skipLoadingState) {
        const loadingItem = {
          id: submission.id,
          type: "screenshot" as const,
          timestamp: Date.now(),
          progress: 0,
        };
        setLoadingItems((prev) => [...prev, loadingItem]);
      }

      let newFeedbackId: string | undefined;

      try {
        setIsCreatingFeedback(true);

        const feedbackResponse = await ApiService.createFeedbackForItem(
          cardId,
          workspaceId,
          "https://example.com",
          "screenshot",
          submission.id,
          originalFeedbackName || feedbackName
        );

        if (feedbackResponse && feedbackResponse.id) {
          newFeedbackId = feedbackResponse.id;
          console.log(
            "✅ Created NEW feedback ID for screenshot:",
            newFeedbackId
          );
        } else {
          throw new Error("Failed to create feedback ID for screenshot");
        }
      } catch (error) {
        console.error(
          "🚨 Failed to create new feedback ID for screenshot:",
          error
        );
        setFeedbackError(`Failed to create feedback entry: ${error}`);
        setLoadingItems((prev) =>
          prev.filter((item) => item.id !== submission.id)
        );
        return;
      } finally {
        setIsCreatingFeedback(false);
      }

      if (newFeedbackId) {
        const uploadPromises = [];

        if (submission.feedback && submission.feedback.trim().length > 0) {
          uploadPromises.push(
            (async () => {
              try {
                await ApiService.uploadMessage(
                  submission.feedback.trim(),
                  newFeedbackId!
                );
                console.log(
                  "✅ Message uploaded successfully to NEW feedback ID"
                );
              } catch (error) {
                console.error("❌ Failed to upload message:", error);
                throw new Error(`Message upload failed: ${error}`);
              }
            })()
          );
        }

        if (submission.annotatedImage) {
          uploadPromises.push(
            (async () => {
              try {
                const response = await fetch(submission.annotatedImage);
                const blob = await response.blob();

                const pngBlob = new Blob([blob], { type: "image/png" });

                console.log("🖼️ Screenshot blob prepared:", {
                  originalSize: blob.size,
                  pngBlobSize: pngBlob.size,
                  type: pngBlob.type,
                });

                await ApiService.uploadFile(
                  pngBlob,
                  `screenshot_${submission.id}_annotated.png`,
                  "screenshot",
                  newFeedbackId!
                );
                console.log(
                  "✅ Screenshot uploaded successfully to NEW feedback ID"
                );
              } catch (error) {
                console.error(
                  "❌ Failed to upload annotated screenshot:",
                  error
                );
                throw new Error(`Screenshot upload failed: ${error}`);
              }
            })()
          );
        }

        if (submission.hasAudio && submission.audioBlob) {
          uploadPromises.push(
            (async () => {
              try {
                const audioBlob = submission.audioBlob!;
                if (audioBlob.size === 0) {
                  throw new Error("Audio blob is empty");
                }
                await ApiService.uploadFile(
                  audioBlob,
                  submission.audioFilename || `audio_${submission.id}.mp3`,
                  "audio",
                  newFeedbackId!
                );
                console.log("✅ Voice recording uploaded successfully");
              } catch (error) {
                console.error("❌ Voice recording upload failed:", error);
                throw new Error(`Voice upload failed: ${error}`);
              }
            })()
          );
        }

        try {
          await Promise.all(uploadPromises);
          console.log(
            "✅ All screenshot content uploaded successfully to NEW feedback ID:",
            newFeedbackId
          );
        } catch (error) {
          console.error("🚨 One or more uploads failed:", error);
          setFeedbackError(`Upload error: ${error}`);
          setTimeout(() => {
            setFeedbackError((prev) =>
              prev.includes("Upload error") ? "" : prev
            );
          }, 5000);

          setLoadingItems((prev) =>
            prev.filter((item) => item.id !== submission.id)
          );
          return;
        }
      }

      setLoadingItems((prev) =>
        prev.map((item) =>
          item.id === submission.id ? { ...item, progress: 100 } : item
        )
      );

      await new Promise((resolve) => setTimeout(resolve, 300));

      const enhancedSubmission: ScreenshotSubmissionEnhanced = {
        ...submission,
        uniqueFeedbackId: newFeedbackId,
      };

      setLoadingItems((prev) =>
        prev.filter((item) => item.id !== submission.id)
      );

      requestAnimationFrame(() => {
        setScreenshots((prev) => [...prev, enhancedSubmission]);
      });

      if (
        isFirstScreenshot &&
        submission.annotatedImage &&
        onFirstScreenshotTaken
      ) {
        console.log(
          "📸 UplaodFeedbackWorkspace - Calling onFirstScreenshotTaken callback with image"
        );
        onFirstScreenshotTaken(submission.annotatedImage);
        setIsFirstScreenshot(false);
      }

      if (!hasAddedFeedback) {
        setHasAddedFeedback(true);
        console.log("✅ First feedback item added - enabling save buttons");
      }

      if (newFeedbackId) {
        setFeedbackId(newFeedbackId);
        console.log(
          "📝 Updated current feedback ID for URL navigation:",
          newFeedbackId
        );
      }
    },
    [
      cardId,
      workspaceId,
      originalFeedbackName,
      feedbackName,
      isFirstScreenshot,
      onFirstScreenshotTaken,
      hasAddedFeedback,
      loadingItems,
    ]
  );

  const handleScreenshotSubmit = useCallback(
    async (submission: ScreenshotSubmission) => {
      console.log(
        "📸 UplaodFeedbackWorkspace - Screenshot submission received:",
        {
          id: submission.id,
          hasAudio: submission.hasAudio,
          uniqueFeedbackId: submission.uniqueFeedbackId,
          isFirstScreenshot: isFirstScreenshot,
        }
      );
      processScreenshotUpload(submission);
    },
    [isFirstScreenshot, processScreenshotUpload]
  );

  const processRecordingUpload = useCallback(
    async (
      submission: RecordingSubmission,
      skipLoadingState: boolean = false
    ) => {
      console.log("📤 Processing recording upload:", {
        id: submission.id,
        duration: submission.duration,
        skipLoadingState,
        hasThumbnail: !!submission.thumbnailUrl,
        hasRecordingBlob: !!submission.recordingBlob,
        blobSize: submission.recordingBlob?.size || 0,
      });

      // ✅ CRITICAL FIX: Validate recordingBlob exists before proceeding
      if (!submission.recordingBlob || submission.recordingBlob.size === 0) {
        console.error("❌ Cannot process recording: Invalid or missing blob", {
          hasBlob: !!submission.recordingBlob,
          blobSize: submission.recordingBlob?.size || 0,
        });

        alert(
          "Recording data is not ready. Please wait a moment and try again."
        );
        return;
      }

      if (!skipLoadingState) {
        const loadingItem = {
          id: submission.id,
          type: "recording" as const,
          timestamp: Date.now(),
          progress: 0,
        };
        setLoadingItems((prev) => [...prev, loadingItem]);
      }

      let newFeedbackId: string | undefined;

      try {
        setIsCreatingFeedback(true);

        const feedbackResponse = await ApiService.createFeedbackForItem(
          cardId,
          workspaceId,
          "https://example.com",
          "recording",
          submission.id,
          originalFeedbackName || feedbackName
        );

        if (feedbackResponse && feedbackResponse.id) {
          newFeedbackId = feedbackResponse.id;
          console.log(
            "✅ Created NEW feedback ID for recording:",
            newFeedbackId
          );
        } else {
          throw new Error("Failed to create feedback ID for recording");
        }
      } catch (error) {
        console.error(
          "🚨 Failed to create new feedback ID for recording:",
          error
        );
        setFeedbackError(`Failed to create feedback entry: ${error}`);
        setLoadingItems((prev) =>
          prev.filter((item) => item.id !== submission.id)
        );
        return;
      } finally {
        setIsCreatingFeedback(false);
      }

      if (newFeedbackId) {
        const uploadPromises = [];

        if (submission.feedback && submission.feedback.trim().length > 0) {
          uploadPromises.push(
            (async () => {
              try {
                await ApiService.uploadMessage(
                  submission.feedback.trim(),
                  newFeedbackId!
                );
                console.log("✅ Recording message uploaded successfully");
              } catch (error) {
                console.error("❌ Failed to upload recording message:", error);
                throw new Error(`Recording message upload failed: ${error}`);
              }
            })()
          );
        }

        // ✅ FIXED: Upload video recording with proper blob validation
        if (submission.recordingBlob && submission.recordingBlob.size > 0) {
          uploadPromises.push(
            (async () => {
              try {
                let videoBlob: Blob = submission.recordingBlob!;
                let fileName = `recording_${submission.id}.mp4`;

                if (!videoBlob.type.includes("mp4")) {
                  console.log("🔄 Converting blob to MP4 MIME type");
                  videoBlob = new Blob([videoBlob], { type: "video/mp4" });
                }

                if (videoBlob.size === 0) {
                  throw new Error("Video blob is empty after processing");
                }

                console.log("📹 Uploading video blob:", {
                  size: videoBlob.size,
                  type: videoBlob.type,
                  fileName,
                });

                const result = await ApiService.uploadFile(
                  videoBlob,
                  fileName,
                  "recording",
                  newFeedbackId!
                );

                console.log(
                  "✅ Recording video uploaded successfully:",
                  result
                );
              } catch (error) {
                console.error("❌ Failed to upload recording video:", error);
                throw new Error(`Recording video upload failed: ${error}`);
              }
            })()
          );
        } else {
          console.error("❌ Cannot upload recording: Blob is invalid");
          setLoadingItems((prev) =>
            prev.filter((item) => item.id !== submission.id)
          );
          setFeedbackError(
            "Recording data is invalid. Please try recording again."
          );
          return;
        }

        if (submission.thumbnailUrl) {
          uploadPromises.push(
            (async () => {
              try {
                const thumbnailResult = await ApiService.uploadVideoThumbnail(
                  submission.thumbnailUrl!,
                  newFeedbackId!
                );
                console.log(
                  "✅ Video thumbnail uploaded successfully:",
                  thumbnailResult
                );
              } catch (error) {
                console.warn("⚠️ Failed to upload video thumbnail:", error);
              }
            })()
          );
        }

        try {
          await Promise.all(uploadPromises);
          console.log("✅ All recording content uploaded successfully");
        } catch (error) {
          console.error("🚨 One or more recording uploads failed:", error);
          setFeedbackError(`Recording upload error: ${error}`);
          setTimeout(() => {
            setFeedbackError((prev) =>
              prev.includes("Recording upload error") ? "" : prev
            );
          }, 5000);
          setLoadingItems((prev) =>
            prev.filter((item) => item.id !== submission.id)
          );
          return;
        }
      }

      setLoadingItems((prev) =>
        prev.map((item) =>
          item.id === submission.id ? { ...item, progress: 100 } : item
        )
      );

      await new Promise((resolve) => setTimeout(resolve, 300));

      const enhancedSubmission: RecordingSubmissionEnhanced = {
        ...submission,
        uniqueFeedbackId: newFeedbackId,
        thumbnailUrl: submission.thumbnailUrl,
      };

      setLoadingItems((prev) =>
        prev.filter((item) => item.id !== submission.id)
      );

      requestAnimationFrame(() => {
        setRecordings((prev) => [...prev, enhancedSubmission]);
      });

      if (!hasAddedFeedback) {
        setHasAddedFeedback(true);
        console.log("✅ First feedback item added - enabling save buttons");
      }

      if (newFeedbackId) {
        setFeedbackId(newFeedbackId);
      }
    },
    [
      cardId,
      workspaceId,
      originalFeedbackName,
      feedbackName,
      hasAddedFeedback,
      loadingItems,
    ]
  );

  const handleRecordingSubmit = useCallback(
    async (submission: RecordingSubmission) => {
      console.log(
        "📹 UplaodFeedbackWorkspace - Recording submission received:",
        {
          id: submission.id,
          duration: submission.duration,
          uniqueFeedbackId: submission.uniqueFeedbackId,
          hasThumbnail: !!submission.thumbnailUrl,
        }
      );

      processRecordingUpload(submission);
    },
    [processRecordingUpload]
  );

  const handleToolClick = useCallback((toolId: string) => {
    console.log("🔧 UplaodFeedbackWorkspace - Tool clicked:", toolId);
  }, []);

  const handleRemoveScreenshot = useCallback(
    (screenshotId: string) => {
      console.log("🗑️ Delete screenshot requested:", screenshotId);
      const screenshot = screenshots.find((s) => s.id === screenshotId);
      setItemToDelete({
        id: screenshotId,
        type: "screenshot",
        uniqueFeedbackId: screenshot?.uniqueFeedbackId,
      });
      setShowDeleteConfirm(true);
    },
    [screenshots]
  );

  const handleEditScreenshot = useCallback(
    async (screenshotId: string) => {
      console.log("✏️ Edit screenshot requested:", screenshotId);
      const screenshot = screenshots.find((s) => s.id === screenshotId);
      if (screenshot) {
        console.log("✏️ Screenshot data being passed to editor:", {
          id: screenshot.id,
          hasAnnotatedImage: !!screenshot.annotatedImage,
          hasOriginalImage: !!screenshot.originalImage,
          hasAudioUrl: !!screenshot.audioUrl,
          hasAudioBlob: !!screenshot.audioBlob,
          audioFilename: screenshot.audioFilename,
        });

        let editAudioUrl = screenshot.audioUrl;
        if (screenshot.audioBlob) {
          if (screenshot.audioUrl?.startsWith("blob:")) {
            URL.revokeObjectURL(screenshot.audioUrl);
          }
          editAudioUrl = URL.createObjectURL(screenshot.audioBlob);
          console.log("✅ Created fresh blob URL for editing:", editAudioUrl);
        }

        setEditingScreenshot({
          ...screenshot,
          annotatedImage: screenshot.annotatedImage,
          originalImage: screenshot.originalImage,
          audioUrl: editAudioUrl,
        });
        setShowEditPreview(true);
      }
    },
    [screenshots]
  );

  const handleEditRecording = useCallback(
    (recordingId: string) => {
      console.log("✏️ Edit recording requested:", recordingId);
      const recording = recordings.find((r) => r.id === recordingId);
      if (recording) {
        console.log("✏️ Recording data being passed to editor:", {
          id: recording.id,
          hasDataUrl: !!recording.dataUrl,
          hasRecordingBlob: !!recording.recordingBlob,
          hasThumbnailUrl: !!recording.thumbnailUrl,
          dataUrlPreview: recording.dataUrl?.substring(0, 50),
        });

        setEditingRecording(recording);
        setShowEditRecordingPreview(true);
      }
    },
    [recordings]
  );

  const handleEditSubmit = useCallback(
    async (submission: ScreenshotSubmission) => {
      console.log(
        "✏️ Screenshot edit submitted - Uploading to SAME feedback ID"
      );

      if (!editingScreenshot) return;

      const feedbackIdToUse = editingScreenshot.uniqueFeedbackId || feedbackId;

      if (!feedbackIdToUse) {
        console.error("❌ No feedback ID available for edit upload");
        setFeedbackError("Cannot update screenshot: No feedback ID found");
        return;
      }

      console.log("📸 Submission image type:", {
        annotatedImage: submission.annotatedImage?.substring(0, 50),
        startsWithData: submission.annotatedImage?.startsWith("data:"),
        startsWithBlob: submission.annotatedImage?.startsWith("blob:"),
        imageType: submission.annotatedImage?.startsWith("data:")
          ? "data:"
          : submission.annotatedImage?.startsWith("blob:")
          ? "blob:"
          : submission.annotatedImage?.startsWith("http")
          ? "remote"
          : "unknown",
      });

      let newAudioUrl = submission.audioUrl;
      if (submission.audioBlob) {
        if (editingScreenshot.audioUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(editingScreenshot.audioUrl);
        }
        newAudioUrl = URL.createObjectURL(submission.audioBlob);
      }

      setScreenshots((prev) =>
        prev.map((s) =>
          s.id === editingScreenshot.id
            ? {
                ...s,
                annotatedImage: submission.annotatedImage,
                originalImage: submission.originalImage,
                feedback: submission.feedback,
                hasAudio: submission.hasAudio,
                audioUrl: newAudioUrl,
                audioBlob: submission.audioBlob,
                audioFilename: submission.audioFilename,
                timestamp: Date.now(),
              }
            : s
        )
      );

      setShowEditPreview(false);
      setEditingScreenshot(null);

      console.log("✅ Screenshot updated instantly in local state");

      const uploads = [];

      if (submission.feedback?.trim()) {
        uploads.push(
          ApiService.uploadMessage(
            submission.feedback.trim(),
            feedbackIdToUse
          ).catch((err) =>
            console.error("Background message upload failed:", err)
          )
        );
      }

      if (submission.annotatedImage) {
        uploads.push(
          (async () => {
            try {
              let pngBlob: Blob;

              console.log("🔄 Processing image for upload...", {
                startsWithData: submission.annotatedImage.startsWith("data:"),
                startsWithBlob: submission.annotatedImage.startsWith("blob:"),
                startsWithHttp: submission.annotatedImage.startsWith("http"),
              });

              if (submission.annotatedImage.startsWith("data:")) {
                console.log("📊 Converting data URL to blob...");
                const parts = submission.annotatedImage.split(",");
                const byteString = atob(parts[1]);
                const mimeString = parts[0].split(":")[1].split(";")[0];
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) {
                  ia[i] = byteString.charCodeAt(i);
                }
                pngBlob = new Blob([ab], { type: mimeString });
                console.log(
                  "✅ Data URL converted to blob:",
                  pngBlob.size,
                  "bytes"
                );
              } else if (submission.annotatedImage.startsWith("blob:")) {
                console.log("📊 Fetching blob URL...");
                const response = await fetch(submission.annotatedImage);
                pngBlob = await response.blob();
                console.log("✅ Blob URL fetched:", pngBlob.size, "bytes");
              } else if (submission.annotatedImage.startsWith("http")) {
                console.warn("⚠️ Remote URL detected - attempting to fetch...");
                try {
                  const response = await fetch(submission.annotatedImage);
                  pngBlob = await response.blob();
                  console.log("✅ Remote URL fetched:", pngBlob.size, "bytes");
                } catch (fetchError) {
                  console.error("❌ Failed to fetch remote URL:", fetchError);
                  throw new Error(
                    "Failed to fetch remote image. Please try again."
                  );
                }
              } else {
                const errorMsg = `Unknown image format: ${submission.annotatedImage.substring(
                  0,
                  50
                )}`;
                console.error("❌", errorMsg);
                throw new Error(errorMsg);
              }

              await ApiService.uploadFile(
                pngBlob,
                `screenshot_${submission.id}_annotated.png`,
                "screenshot",
                feedbackIdToUse
              );
              console.log(
                "✅ Edited screenshot uploaded successfully to SAME feedback ID"
              );
            } catch (err) {
              console.error("Background screenshot upload failed:", err);
            }
          })()
        );
      }

      if (submission.hasAudio && submission.audioBlob) {
        uploads.push(
          ApiService.uploadFile(
            submission.audioBlob,
            submission.audioFilename || `audio_${submission.id}.mp3`,
            "audio",
            feedbackIdToUse
          ).catch((err) =>
            console.error("Background audio upload failed:", err)
          )
        );
      }

      Promise.all(uploads).then(() =>
        console.log(
          "✅ All background uploads complete to feedback ID:",
          feedbackIdToUse
        )
      );
    },
    [editingScreenshot, feedbackId]
  );

  const handleEditRecordingSubmit = useCallback(
    async (submission: RecordingSubmission) => {
      console.log("✏️ Recording edit submitted");

      if (!editingRecording) return;

      const feedbackIdToUse = editingRecording.uniqueFeedbackId || feedbackId;

      setRecordings((prev) =>
        prev.map((r) =>
          r.id === editingRecording.id
            ? {
                ...r,
                dataUrl: submission.dataUrl || r.dataUrl,
                feedback: submission.feedback,
                recordingBlob: submission.recordingBlob || r.recordingBlob,
                thumbnailUrl: submission.thumbnailUrl || r.thumbnailUrl,
                timestamp: Date.now(),
              }
            : r
        )
      );

      setShowEditRecordingPreview(false);
      setEditingRecording(null);

      console.log("✅ Recording updated instantly in local state");

      if (feedbackIdToUse && submission.feedback?.trim()) {
        ApiService.uploadMessage(
          submission.feedback.trim(),
          feedbackIdToUse
        ).catch((err) =>
          console.error("Background message upload failed:", err)
        );
      }
    },
    [editingRecording, feedbackId]
  );

  const handleRemoveRecording = useCallback(
    (recordingId: string) => {
      console.log("🗑️ Delete recording requested:", recordingId);
      const recording = recordings.find((r) => r.id === recordingId);
      setItemToDelete({
        id: recordingId,
        type: "recording",
        uniqueFeedbackId: recording?.uniqueFeedbackId,
      });
      setShowDeleteConfirm(true);
    },
    [recordings]
  );

  const handlePlayRecording = useCallback(
    (recordingId: string) => {
      console.log("▶️ Playing recording:", recordingId);

      const recording = recordings.find((r) => r.id === recordingId);
      if (!recording || !recording.dataUrl) {
        console.error("Recording not found or no dataUrl available");
        return;
      }

      const video = document.createElement("video");
      video.src = recording.dataUrl;
      video.controls = true;
      video.autoplay = true;
      video.style.maxWidth = "90vw";
      video.style.maxHeight = "90vh";

      const modal = document.createElement("div");
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      `;

      const closeButton = document.createElement("button");
      closeButton.textContent = "×";
      closeButton.style.cssText = `
        position: absolute;
        top: 20px;
        right: 30px;
        background: rgba(255, 255, 255, 0.9);
        border: none;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        font-size: 24px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
      `;

      closeButton.onclick = () => {
        document.body.removeChild(modal);
        video.pause();
        if (video.src.startsWith("blob:")) {
          URL.revokeObjectURL(video.src);
        }
      };

      modal.appendChild(video);
      modal.appendChild(closeButton);
      document.body.appendChild(modal);

      modal.onclick = (e) => {
        if (e.target === modal) {
          closeButton.click();
        }
      };
    },
    [recordings]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!itemToDelete) return;

    const { id, type, uniqueFeedbackId } = itemToDelete;

    console.log("🗑️ Confirming delete:", { id, type, uniqueFeedbackId });

    setShowDeleteConfirm(false);
    setItemToDelete(null);

    if (uniqueFeedbackId) {
      try {
        const accessToken = EnhancedTokenManager.getAccessToken();
        if (!accessToken) {
          console.error("No access token available for deletion");
        } else {
          const numericFeedbackId = String(uniqueFeedbackId)
            .replace(/^feedback_/, "")
            .trim();

          console.log("🗑️ Deleting from database:", numericFeedbackId);

          const response = await fetch(
            API_URLS.DELETE_FILE(numericFeedbackId),
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) {
            console.error("Failed to delete from database:", response.status);
          } else {
            console.log("✅ Successfully deleted from database");
          }
        }
      } catch (error) {
        console.error("❌ Error deleting from database:", error);
      }
    }

    if (type === "screenshot") {
      setScreenshots((prev) => {
        const screenshot = prev.find((s) => s.id === id);
        if (screenshot?.audioUrl && screenshot.audioUrl.startsWith("blob:")) {
          URL.revokeObjectURL(screenshot.audioUrl);
        }
        return prev.filter((s) => s.id !== id);
      });
      console.log("✅ Screenshot removed from local state");
    } else if (type === "recording") {
      setRecordings((prev) => {
        const recording = prev.find((r) => r.id === id);
        if (recording?.dataUrl && recording.dataUrl.startsWith("blob:")) {
          URL.revokeObjectURL(recording.dataUrl);
        }
        return prev.filter((r) => r.id !== id);
      });
      console.log("✅ Recording removed from local state");
    }

    setSuccessMessageText(
      `${
        type === "screenshot" ? "Screenshot" : "Recording"
      } deleted successfully!`
    );
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  }, [itemToDelete]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  }, []);

  const handleSaveAndExitWithLatestData = useCallback(() => {
    console.log("💾 Saving with LATEST data before navigation");

    const latestScreenshots = screenshots;
    const latestRecordings = recordings;

    const latestFeedbackItems: FeedbackItem[] = feedbackItemsOrder
      .map((itemKey) => {
        if (itemKey.startsWith("screenshot-")) {
          const screenshot = latestScreenshots.find(
            (s) => `screenshot-${s.id}` === itemKey
          );
          if (!screenshot) return null;

          const item: FeedbackItem = {
            id: screenshot.id,
            type: "screenshot" as const,
            timestamp: screenshot.timestamp,
            order: feedbackItemsOrder.indexOf(itemKey),
            annotatedImage: screenshot.annotatedImage,
            originalImage: screenshot.originalImage,
            feedback: screenshot.feedback,
            hasAudio: screenshot.hasAudio || false,
            audioUrl: screenshot.audioUrl,
            audioFilename: screenshot.audioFilename,
            uniqueFeedbackId: screenshot.uniqueFeedbackId || screenshot.id,
          };
          return item;
        } else if (itemKey.startsWith("recording-")) {
          const recording = latestRecordings.find(
            (r) => `recording-${r.id}` === itemKey
          );
          if (!recording) return null;

          const item: FeedbackItem = {
            id: recording.id,
            type: "recording" as const,
            timestamp: recording.timestamp,
            order: feedbackItemsOrder.indexOf(itemKey),
            dataUrl: recording.dataUrl,
            recordingBlob: recording.recordingBlob,
            duration: recording.duration,
            feedback: recording.feedback,
            thumbnailUrl: recording.thumbnailUrl,
            uniqueFeedbackId: recording.uniqueFeedbackId || recording.id,
          };
          return item;
        }
        return null;
      })
      .filter((item): item is FeedbackItem => item !== null);

    const workspaceStateData = {
      screenshots: latestScreenshots,
      recordings: latestRecordings,
      feedbackItemsOrder,
      feedbackId,
      isUrlLocked,
    };

    console.log("✅ Passing FRESH data to ViewListFeedback:", {
      screenshotCount: latestScreenshots.length,
      recordingCount: latestRecordings.length,
      totalItems: latestFeedbackItems.length,
    });

    onSaveAndExit(latestFeedbackItems, workspaceStateData);
  }, [
    screenshots,
    recordings,
    feedbackItemsOrder,
    feedbackId,
    isUrlLocked,
    onSaveAndExit,
  ]);

  const handleSaveAndExit = useCallback(() => {
    console.log("💾 UplaodFeedbackWorkspace - Saving and exiting with data:", {
      screenshots: screenshots.length,
      recordings: recordings.length,
      totalItems: allFeedbackItems.length,
      feedbackId: feedbackId,
      isUrlLocked: isUrlLocked,
      uniqueFeedbackIds: [
        ...screenshots.map((s) => s.uniqueFeedbackId).filter(Boolean),
        ...recordings.map((r) => r.uniqueFeedbackId).filter(Boolean),
      ],
    });

    const workspaceStateData = {
      screenshots,
      recordings,
      feedbackItemsOrder,
      feedbackId,
      isUrlLocked,
    };

    onSaveAndExit(allFeedbackItems, workspaceStateData);
  }, [
    allFeedbackItems,
    feedbackItemsOrder,
    screenshots,
    recordings,
    feedbackId,
    isUrlLocked,
    onSaveAndExit,
  ]);

  const handleSaveDraft = useCallback(() => {
    console.log("📝 UplaodFeedbackWorkspace - Saving draft with data:", {
      screenshots: screenshots.length,
      recordings: recordings.length,
      totalItems: allFeedbackItems.length,
      feedbackId: feedbackId,
      isUrlLocked: isUrlLocked,
    });

    const workspaceStateData = {
      screenshots,
      recordings,
      feedbackItemsOrder,
      feedbackId,
      isUrlLocked,
      shouldRefreshDrafts: true,
    };

    onSaveDraft(allFeedbackItems, workspaceStateData);
  }, [
    allFeedbackItems,
    feedbackItemsOrder,
    screenshots,
    recordings,
    feedbackId,
    isUrlLocked,
    onSaveDraft,
  ]);

  const handleToggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
    setShowDropdown(false);
  }, [isMuted]);

  const handleDeleteCard = useCallback(async () => {
    if (!cardId) {
      console.error("No card ID available for deletion");
      return;
    }

    setIsDeletingCard(true);

    try {
      console.log("🗑️ Deleting card:", cardId);

      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available");
      }

      const response = await fetch(API_URLS.DELETE_CARD(cardId), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete card: ${errorText}`);
      }

      console.log("✅ Card deleted successfully");

      setShowDeleteCardConfirm(false);

      setSuccessMessageText(`"${feedbackName}" has been deleted successfully!`);
      setShowSuccessMessage(true);

      setTimeout(() => {
        onBack({
          cardDeleted: true,
          deletedCardId: cardId,
          deletedCardName: feedbackName,
          activeTab: "draft",
        });
      }, 1500);
    } catch (error) {
      console.error("❌ Error deleting card:", error);
      alert(
        `Failed to delete card: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setIsDeletingCard(false);
    }
  }, [cardId, feedbackName, onBack]);

  const handleDeleteButtonClick = useCallback(() => {
    setShowDeleteCardConfirm(true);
  }, []);

  return (
    <div className="w-full min-h-screen bg-white flex flex-col relative">
      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-green-800">
                  Delete Successful!
                </p>
                <p className="text-sm text-green-700 mt-1">
                  {successMessageText}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0">
                <button
                  onClick={() => setShowSuccessMessage(false)}
                  className="bg-green-50 rounded-md inline-flex text-green-400 hover:text-green-500 focus:outline-none"
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {hoveredItemId &&
        (() => {
          const hoveredItem = allFeedbackItems.find(
            (item) => `${item.type}-${item.id}` === hoveredItemId
          );

          if (!hoveredItem) return null;

          const hoveredIndex = allFeedbackItems.findIndex(
            (item) => `${item.type}-${item.id}` === hoveredItemId
          );

          const hoveredElement = document.querySelector(
            `[data-item-index="${hoveredIndex}"]`
          );
          const hoveredRect = hoveredElement?.getBoundingClientRect();

          if (!hoveredRect) return null;

          const screenAspectRatio = window.innerWidth / window.innerHeight;
          const maxPreviewWidth = 420;
          const previewWidth = Math.min(
            maxPreviewWidth,
            window.innerWidth * 0.25
          );
          const previewHeight = previewWidth / screenAspectRatio;

          let finalTop = hoveredRect.top;
          const viewportHeight = window.innerHeight;
          const bottomPadding = 52;
          const previewBottom = finalTop + previewHeight;

          if (previewBottom > viewportHeight - bottomPadding) {
            const overflow = previewBottom - (viewportHeight - bottomPadding);
            finalTop = finalTop - overflow;
            if (finalTop < 40) finalTop = 40;
          }

          const getLeftPosition = () => {
            const width = window.innerWidth;
            if (width >= 2300) {
              return "calc(214px + 1rem )";
            } else if (width >= 1920) {
              return "calc(160px + 0.75rem )";
            } else if (width >= 1536) {
              return "calc(128px + 0.5rem )";
            } else if (width >= 1280) {
              return "calc(128px + 0.5rem )";
            } else if (width >= 640) {
              return "calc(64px + 0.5rem )";
            } else {
              return "calc(48px + 0.5rem )";
            }
          };

          return (
            <div
              className="fixed bg-white border-[1.5px] border-[#5A5A5A] rounded-[12px] shadow-2xl overflow-hidden pointer-events-none"
              style={{
                animation: "fadeIn 0.2s ease-in-out",
                left: getLeftPosition(),
                top: `${finalTop}px`,
                width: `${previewWidth}px`,
                height: `${previewHeight}px`,
                maxHeight: "calc(100vh - 120px)",
                zIndex: 10000,
              }}
            >
              {hoveredItem.type === "screenshot" ? (
                <div className="relative h-full">
                  <div className=" h-full flex items-center justify-center">
                    <img
                      src={hoveredItem.annotatedImage}
                      alt="Preview"
                      className="w-full h-full  object-cover"
                    />
                  </div>
                </div>
              ) : (
                <div className="relative h-full">
                  {hoveredItem.thumbnailUrl ? (
                    <div className="relative bg-gray-100 h-full flex items-center justify-center">
                      <img
                        src={hoveredItem.thumbnailUrl}
                        alt="Recording preview"
                        className="w-full h-full object-contain"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                        <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                          <svg
                            className="w-8 h-8 text-black ml-1"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-20 h-20 mx-auto bg-gray-700 rounded-full flex items-center justify-center mb-3">
                          <svg
                            className="w-10 h-10 text-white"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

      <header className="bg-white px-3 sm:px-4 xl:px-6 2xl:px-8 4xl:px-10 py-2 sm:py-3 xl:py-3 2xl:py-4 4xl:py-6 w-full">
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1 mr-4">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center px-1 py-1 bg-[#DFE1E8] rounded-[6px] transition-colors mr-4"
              >
                <div className="relative w-5 h-5 4xl:w-6 4xl:h-6">
                  <DownArrow
                    className={`absolute inset-0 w-full h-full transition-all duration-300 ease-in-out ${
                      showDropdown
                        ? "opacity-0 scale-0 rotate-90"
                        : "opacity-100 scale-100 rotate-0"
                    }`}
                  />
                  <CloseLogo
                    className={`absolute inset-0 w-full h-full transition-all duration-300 ease-in-out ${
                      showDropdown
                        ? "opacity-100 scale-100 rotate-0"
                        : "opacity-0 scale-0 -rotate-90"
                    }`}
                  />
                </div>
              </button>

              {/* Dropdown Content */}
              {showDropdown && (
                <div className="absolute top-full left-0 p-2 mt-2 w-56 bg-[#FCFDFE] border border-[#E6E8EE] rounded-[10px] shadow-lg z-50">
                  <button
                    onClick={handleGoToHome}
                    className="w-full px-4 py-3 text-left text-sm 4xl:text-base font-medium font-inter text-[#1E1F26] hover:bg-[#F4F4F4] rounded-[10px] flex items-center gap-3 transition-colors"
                  >
                    <Home className="w-6 h-6 4xl:w-6 4xl:h-6" />
                    <span className="font-medium">Home</span>
                    <kbd className="ml-auto text-xs 4xl:text-sm font-medium font-inter text-[#1E1F26]">
                      H
                    </kbd>
                  </button>

                  <button
                    onClick={() => {
                      // setShowDropdown(false);
                      // // Toggle fullscreen logic here
                      // if (!document.fullscreenElement) {
                      //   document.documentElement.requestFullscreen();
                      // } else {
                      //   document.exitFullscreen();
                      // }
                    }}
                    className="w-full px-4 py-3 text-left text-sm 4xl:text-base font-medium font-inter text-[#1E1F26] hover:bg-[#F4F4F4] rounded-[10px] flex items-center gap-3 transition-colors"
                  >
                    <FullScreenLogo className="w-6 h-6 4xl:w-6 4xl:h-6" />
                    <span className="font-medium">Full screen</span>
                    <kbd className="ml-auto text-xs 4xl:text-sm font-medium font-inter text-[#1E1F26]">
                      F
                    </kbd>
                  </button>

                  <button
                    onClick={handleToggleMute}
                    className="w-full px-4 py-3 text-left text-sm 4xl:text-base font-medium font-inter text-[#1E1F26] hover:bg-[#F4F4F4] rounded-[10px] flex items-center gap-3 transition-colors"
                  >
                    {isMuted ? (
                      // Muted icon (with X)
                      <UnmuteLogo className="w-6 h-6 4xl:w-6 4xl:h-6" />
                    ) : (
                      // Unmuted icon (with sound waves)
                      <MuteLogo className="w-6 h-6 4xl:w-6 4xl:h-6" />
                    )}
                    <span className="font-medium">
                      {isMuted ? "Unmute" : "Mute"}
                    </span>
                    <kbd className="ml-auto text-xs 4xl:text-sm font-medium font-inter text-[#1E1F26]">
                      M
                    </kbd>
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <h1 className="text-sm sm:text-base xl:text-lg 2xl:text-xl 3xl:text-2xl 4xl:text-3xl font-medium text-gray-900 truncate">
                {feedbackName}
              </h1>
            </div>
          </div>

          {/* Right side - Action buttons */}
          <div className="flex items-center gap-2 sm:gap-3 xl:gap-3 2xl:gap-4 4xl:gap-5 flex-shrink-0">
            {!hasAddedFeedback && (
              <button
                className="text-red-500 hover:text-red-700 transition p-1 rounded"
                onClick={handleDeleteButtonClick}
                title="Delete card"
                disabled={isDeletingCard}
              >
                <DeleteIcon className="w-5 h-5 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 4xl:w-8 4xl:h-8" />
              </button>
            )}
            <Button
              variant="cancel"
              size="md"
              onClick={handleSaveDraft}
              disabled={!hasAddedFeedback}
              className={`text-xs sm:text-sm xl:text-sm 2xl:text-base 4xl:text-xl px-2 sm:px-3 xl:px-4 2xl:px-5 4xl:px-6 py-1 sm:py-1.5 xl:py-2 2xl:py-2.5 4xl:py-3 ${
                !hasAddedFeedback ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              Save as draft
            </Button>

            <Button
              variant="primary"
              size="md"
              onClick={handleSaveAndExitWithLatestData}
              disabled={!hasAddedFeedback}
              className={`text-xs sm:text-sm xl:text-sm 2xl:text-base 4xl:text-xl px-2 sm:px-3 xl:px-4 2xl:px-5 4xl:px-6 py-1 sm:py-1.5 xl:py-2 2xl:py-2.5 4xl:py-3 ${
                !hasAddedFeedback ? "opacity-50 cursor-not-allowed" : ""
              }`}
              data-tour="view-list"
            >
              View list ({totalFeedbackItems})
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="flex flex-1">
        {/* Left Sidebar */}
        <aside className="ml-2 3xl:ml-3 w-12 sm:w-16 xl:w-32 2xl:w-32 3xl:w-40 4xl:w-[214px] bg-[#F7F7F7] border-x border-t  border-[#D7D9E2] rounded-t-[10px] flex flex-col items-center relative ">
          {/* UPDATED: Feedback Items Container with video thumbnails */}
          <div
            ref={sidebarItemsRef}
            className="flex flex-col mt-2  gap-2 sm:gap-3 xl:gap-3 2xl:gap-3 4xl:gap-4 w-full max-h-[calc(100vh-150px)] pl-[10px] xl:pl-[12px] 3xl:pl-[11px] 4xl:pl-[15px] pb-3 overflow-y-auto overflow-x-hidden custom-scrollbar-overlay"
            // style={{ paddingBottom: "100px" }}
          >
            <style>{`
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  /* ✨ NEW: Smooth progress bar transition */
  .progress-bar-fill {
    transition: width 0.3s ease-out;
  }

  @keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-10px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(10px);
  }
}

.shake-animation {
  animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both;
}

`}</style>

            {allFeedbackItems.map((item, index) => {
              const itemKey = `${item.type}-${item.id}`;
              const isHovered = hoveredItemId === itemKey;

              return (
                <div
                  key={itemKey}
                  onMouseEnter={() => setHoveredItemId(itemKey)}
                  onMouseLeave={() => setHoveredItemId(null)}
                  data-item-index={index}
                  title={
                    item.uniqueFeedbackId
                      ? `Feedback ID: ${item.uniqueFeedbackId}`
                      : `${item.type} ${item.id}`
                  }
                  className={`relative group w-10 h-6 sm:w-12 sm:h-8 xl:w-[104px] xl:h-[60px] 2xl:w-[104px] 2xl:h-[60px]   3xl:w-[138px] 3xl:h-[80px] 4xl:w-[184px] 4xl:h-[106px] border border-gray-200 rounded-[6px] overflow-visible shadow-sm hover:shadow-md transition-all duration-200 flex-shrink-0`}
                  style={{
                    zIndex: isHovered ? 200 : "auto",
                  }}
                >
                  {item.type === "screenshot" ? (
                    <>
                      <img
                        src={item.annotatedImage}
                        alt={`Screenshot ${index + 1}`}
                        className="w-full h-full object-cover rounded-[4px] pointer-events-none"
                      />
                      {/* ✅ REPLACE THE ENTIRE div.absolute BLOCK WITH THIS */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center gap-2">
                        {/* Edit Icon  */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditScreenshot(item.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 text-white w-6 h-6 sm:w-7 sm:h-7 xl:w-8 xl:h-8 2xl:w-10 2xl:h-10 4xl:w-12 4xl:h-12 flex items-center justify-center transition-all duration-200"
                          title="Edit screenshot"
                        >
                          <EditPreview className="w-8 h-8 xl:w-14 xl:h-14 3xl:w-16 3xl:h-16" />
                        </button>

                        {/* Delete Icon */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveScreenshot(item.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 text-white w-6 h-6 sm:w-7 sm:h-7 xl:w-8 xl:h-8 2xl:w-10 2xl:h-10 4xl:w-12 4xl:h-12 flex items-center justify-center transition-all duration-200"
                          title="Remove screenshot"
                        >
                          <Delete className="w-8 h-8 xl:w-14 xl:h-14 3xl:w-16 3xl:h-16" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Existing thumbnail/play icon code stays the same */}
                      {item.thumbnailUrl ? (
                        <div
                          className="relative w-full h-full rounded-[4px] cursor-pointer"
                          onClick={() => handlePlayRecording(item.id)}
                        >
                          <img
                            src={item.thumbnailUrl}
                            alt={`Recording ${index + 1} thumbnail`}
                            className="w-full h-full object-cover pointer-events-none rounded-[4px]"
                            onError={(e) => {
                              console.warn(
                                `Failed to load thumbnail for ${item.id}`
                              );
                              e.currentTarget.style.display = "none";
                            }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-[4px]">
                            <div className="w-4 h-4 sm:w-4 sm:h-4 xl:w-6 xl:h-6 2xl:w-8 2xl:h-8 4xl:w-10 4xl:h-10 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                              <svg
                                className="w-3 h-3 sm:w-3 sm:h-3 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 4xl:w-6 4xl:h-6 text-black ml-0.5"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-[4px] cursor-pointer"
                          onClick={() => handlePlayRecording(item.id)}
                        >
                          <div className="w-3 h-3 sm:w-4 sm:h-4 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 4xl:w-8 4xl:h-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <svg
                              className="w-2 h-2 sm:w-2.5 sm:h-2.5 xl:w-3 xl:h-3 2xl:w-3.5 2xl:h-3.5 4xl:w-4 4xl:h-4 text-black ml-0.5"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      )}

                      <div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-70 transition-opacity">
                        <svg
                          className="w-2 h-2 sm:w-3 sm:h-3 text-gray-600"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 6h2v2H8zm0 4h2v2H8zm0 4h2v2H8zm4-8h2v2h-2zm0 4h2v2h-2zm0 4h2v2h-2z" />
                        </svg>
                      </div>

                      {/* ✅ REPLACE THIS ENTIRE DIV - ADD EDIT AND DELETE BUTTONS */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center gap-2">
                        {/* Edit Icon */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditRecording(item.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 text-white w-6 h-6 sm:w-7 sm:h-7 xl:w-8 xl:h-8 2xl:w-10 2xl:h-10 4xl:w-12 4xl:h-12 flex items-center justify-center transition-all duration-200"
                          title="Edit recording"
                        >
                          <EditPreview className="w-8 h-8 xl:w-14 xl:h-14 3xl:w-16 3xl:h-16" />
                        </button>

                        {/* Delete Icon */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveRecording(item.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 text-white w-6 h-6 sm:w-7 sm:h-7 xl:w-8 xl:h-8 2xl:w-10 2xl:h-10 4xl:w-12 4xl:h-12 flex items-center justify-center transition-all duration-200"
                          title="Remove recording"
                        >
                          <Delete className="w-8 h-8 xl:w-14 xl:h-14 3xl:w-16 3xl:h-16" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
            {loadingItems.map((loadingItem) => (
              <div
                key={`loading-${loadingItem.id}`}
                className="w-10 h-6 sm:w-12 sm:h-8 xl:w-[104px] xl:h-[60px] 2xl:w-[104px] 2xl:h-[60px]   3xl:w-[138px] 3xl:h-[80px] 4xl:w-[184px] 4xl:h-[106px] border border-gray-300 rounded-[6px] overflow-hidden shadow-sm flex-shrink-0 bg-gradient-to-r from-gray-100 to-gray-200"
                style={{
                  opacity: loadingItem.progress === 100 ? 0 : 1,
                  transition: "opacity 0.5s ease-out",
                }}
              >
                <div className="w-full h-full flex flex-col items-center justify-center p-2">
                  {/* Progress Bar with actual percentage */}
                  <div className="w-full h-1 bg-gray-300 rounded-full overflow-hidden ">
                    <div
                      className="h-full bg-[#1E1F26] rounded-full progress-bar-fill"
                      style={{
                        width: `${loadingItem.progress}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}

            {/* Feedback Items Container - BEFORE this change */}
            {totalFeedbackItems === 0 && loadingItems.length === 0 && (
              <div className="w-10 h-6 sm:w-12 sm:h-8 xl:w-[104px] xl:h-[60px] 2xl:w-[104px] 2xl:h-[60px]   3xl:w-[138px] 3xl:h-[80px] 4xl:w-[184px] 4xl:h-[106px] bg-[#F8F9FC] rounded-[6px] border border-[#B7BBCA] flex-shrink-0" />
            )}
          </div>

          {/* ScreenshotManager Section - Always visible, URL validation in ToolBox */}
          <div
            className="absolute bottom-0 left-0 right-0 bg-[#F7F7F7] border-t border-[#D7D9E2]"
            data-tour="screenshot-tool"
          >
            <div className="w-full py-2 flex items-center justify-center bg-white">
              <ScreenshotManager
                onToolClick={handleToolClick}
                onScreenshotSubmit={handleScreenshotSubmit}
                onRecordingSubmit={handleRecordingSubmit}
                uniqueFeedbackId={feedbackId}
                isMuted={isMuted}
                cardId={cardId}
              />
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 flex flex-col">
          <div className="flex-1 flex flex-col mx-2 mb-2 relative border border-gray-200 rounded-xl overflow-hidden">
            {/* Centered Upload Container */}
            <div className="flex-1 flex items-center justify-center ">
              <div className="bg-[#F5F5F5] rounded-2xl p-8 flex flex-col items-center justify-center max-w-md w-full">
                {/* Upload Icon */}
                <div className="mb-4">
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>

                {/* Upload Text */}
                <p className="text-gray-600 text-base font-medium mb-4 text-center">
                  Drag here or upload file here
                </p>

                {/* Upload Button */}
                <Button variant="primary" className="px-8 py-3">
                  Upload file
                </Button>
              </div>
            </div>

            {/* Delete Confirmation Popup */}
            {showDeleteConfirm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-lg 2xl:max-w-xl 4xl:max-w-2xl w-full mx-4">
                  <div className="mx-4 sm:mx-4 xl:mx-6 2xl:mx-6 4xl:mx-12 py-3 sm:py-3 xl:py-4 2xl:py-4 4xl:py-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mt-4 mb-4">
                      <h3 className="text-sm sm:text-base xl:text-xl 2xl:text-2xl 3xl:text-2xl 4xl:text-3xl font-semibold font-inter text-[#070707] text-left">
                        Are you sure?
                      </h3>
                      <button
                        onClick={handleDeleteCancel}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          className="w-5 h-5 3xl:w-6 3xl:h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Separator */}
                    <div className="border-t border-gray-200 mb-4" />

                    {/* Description */}
                    <p className="text-[#070707] text-sm sm:text-sm xl:text-base 2xl:text-base 3xl:text-lg 4xl:text-xl font-inter mb-10">
                      This action cannot be undone. All values associated with
                      this will be lost.
                    </p>

                    {/* Separator */}
                    <div className="border-t border-gray-200  mb-6" />

                    {/* Actions */}
                    <div className="flex justify-end gap-3">
                      <Button
                        variant="cancel"
                        className="w-full sm:w-auto text-xs sm:text-sm xl:text-base 2xl:text-base 3xl:text-xl 4xl:text-2xl px-4 sm:px-6 xl:px-8 2xl:px-10 4xl:px-12 py-2 sm:py-2 xl:py-2 2xl:py-3 4xl:py-4"
                        onClick={handleDeleteCancel}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        className="w-full sm:w-auto text-xs sm:text-sm xl:text-base 2xl:text-base 3xl:text-xl 4xl:text-2xl px-4 sm:px-6 xl:px-8 2xl:px-10 4xl:px-12 py-2 sm:py-2 xl:py-2 2xl:py-3 4xl:py-4 bg-[#FF4D40]"
                        onClick={handleDeleteConfirm}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Card Confirmation Popup */}
            {showDeleteCardConfirm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-lg 2xl:max-w-xl 4xl:max-w-2xl w-full mx-4">
                  <div className="mx-4 sm:mx-4 xl:mx-6 2xl:mx-6 4xl:mx-12 py-3 sm:py-3 xl:py-4 2xl:py-4 4xl:py-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mt-4 mb-4">
                      <h3 className="text-sm sm:text-base xl:text-xl 2xl:text-2xl 3xl:text-2xl 4xl:text-3xl font-semibold font-inter text-[#070707] text-left">
                        Discard project?
                      </h3>
                      <button
                        onClick={() => setShowDeleteCardConfirm(false)}
                        className="text-gray-400 hover:text-gray-600"
                        disabled={isDeletingCard}
                      >
                        <svg
                          className="w-5 h-5 3xl:w-6 3xl:h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Separator */}
                    <div className="border-t border-gray-200 mb-4" />

                    {/* Description */}
                    <p className="text-[#070707] text-sm sm:text-sm xl:text-base 2xl:text-base 3xl:text-lg 4xl:text-xl font-inter mb-10">
                      Are you sure you want to discard the project? This step
                      will not be revert back.
                    </p>

                    {/* Separator */}
                    <div className="border-t border-gray-200 mb-6" />

                    {/* Actions */}
                    <div className="flex justify-end gap-3">
                      <Button
                        variant="cancel"
                        className="w-full sm:w-auto text-xs sm:text-sm xl:text-base 2xl:text-base 3xl:text-xl 4xl:text-2xl px-4 sm:px-6 xl:px-8 2xl:px-10 4xl:px-12 py-2 sm:py-2 xl:py-2 2xl:py-3 4xl:py-4"
                        onClick={() => setShowDeleteCardConfirm(false)}
                        disabled={isDeletingCard}
                      >
                        Continue
                      </Button>
                      <Button
                        variant="primary"
                        className="w-full sm:w-auto text-xs sm:text-sm xl:text-base 2xl:text-base 3xl:text-xl 4xl:text-2xl px-4 sm:px-6 xl:px-8 2xl:px-10 4xl:px-12 py-2 sm:py-2 xl:py-2 2xl:py-3 4xl:py-4 bg-[#FF4D40]"
                        onClick={handleDeleteCard}
                        disabled={isDeletingCard}
                      >
                        {isDeletingCard ? "Deleting..." : "Yes, Discard"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Screenshot Edit Preview */}
            {showEditPreview && editingScreenshot && (
              <ScreenshotPreviewComponent
                preview={{
                  dataUrl:
                    editingScreenshot.annotatedImage ||
                    editingScreenshot.originalImage,
                  area: {
                    startX: 0,
                    startY: 0,
                    endX: 0,
                    endY: 0,
                  },
                  actualWidth: 0,
                  actualHeight: 0,
                }}
                isLoading={false}
                onClose={() => {
                  setShowEditPreview(false);
                  setEditingScreenshot(null);
                }}
                onDownload={() => {
                  // Optional: Add download functionality if needed
                }}
                onSubmit={handleEditSubmit}
                uniqueFeedbackId={
                  editingScreenshot.uniqueFeedbackId || feedbackId
                }
                isEditMode={true}
                existingFeedback={editingScreenshot.feedback || ""}
                existingAudioUrl={editingScreenshot.audioUrl}
                existingAudioBlob={editingScreenshot.audioBlob}
                existingAudioFilename={editingScreenshot.audioFilename}
              />
            )}

            {/* Recording Edit Preview */}
            {showEditRecordingPreview && editingRecording && (
              <ScreenRecordingPreview
                preview={{
                  dataUrl: editingRecording.dataUrl,
                  blob:
                    editingRecording.recordingBlob ||
                    new Blob([], { type: "video/mp4" }), // ✅ FIX: Provide fallback Blob
                  duration: editingRecording.duration,
                  timestamp: editingRecording.timestamp,
                }}
                onClose={() => {
                  setShowEditRecordingPreview(false);
                  setEditingRecording(null);
                }}
                onDownload={() => {
                  console.log("Download recording:", editingRecording.id);
                }}
                onSubmit={handleEditRecordingSubmit}
                uniqueFeedbackId={
                  editingRecording.uniqueFeedbackId || feedbackId
                }
                isEditMode={true}
                existingFeedback={editingRecording.feedback || ""}
                existingRecordingBlob={editingRecording.recordingBlob} // ✅ This is still optional, which is fine
                existingDataUrl={editingRecording.dataUrl}
                existingThumbnailUrl={editingRecording.thumbnailUrl}
              />
            )}
          </div>
        </main>
      </div>
      {/* Picture-in-Picture Minimized Workspace */}
      {isPiPMode && pipWorkspaceData && (
        <MinimizedWorkspace
          feedbackName={pipWorkspaceData.feedbackName}
          previewImage={pipWorkspaceData.previewImage}
          totalFeedbacks={pipWorkspaceData.feedbackItems.length}
          /* Maximize: Return to full workspace */
          onMaximize={handleMaximizeFromPiP}
          /* Close: Only called AFTER successful save */
          onClose={() => {
            setIsPiPMode(false);
            setPipWorkspaceData(null);
            console.log("✅ Upload PiP closed after successful save");
          }}
          /* Save Draft: Saves and triggers onClose */
          onSaveDraft={() => {
            console.log("💾 Saving draft from upload PiP mode...");

            if (pipWorkspaceData) {
              const workspaceStateData = {
                screenshots: pipWorkspaceData.workspaceState?.screenshots || [],
                recordings: pipWorkspaceData.workspaceState?.recordings || [],
                feedbackItemsOrder:
                  pipWorkspaceData.workspaceState?.feedbackItemsOrder || [],
                feedbackId: pipWorkspaceData.workspaceState?.feedbackId,
                isUrlLocked: pipWorkspaceData.workspaceState?.isUrlLocked,
                shouldRefreshDrafts: true,
              };

              onSaveDraft(pipWorkspaceData.feedbackItems, workspaceStateData);
              console.log("✅ Draft saved successfully from upload PiP mode");
            }
          }}
        />
      )}
    </div>
  );
};

export default UplaodFeedbackWorkspace;
